'use client';

import React from 'react';
import { getOracleOffsetLabel } from 'src/utils/oracle';
import { formatText } from '@velocity-exchange/common/format';
import { PRESETS, withDecimals } from 'src/utils/formatNumber/presets';
import { TradeFormDetails } from './types';
import TradeConfirmationModalWrapper from 'src/components/TradeConfirmationModal/TradeConfirmationModalWrapper';
import { TRADE_CONFIRMATION_MODAL_DETAILS_CONTAINER_CLASSNAME } from 'src/components/TradeConfirmationModal/consts';
import ValueDisplay from 'src/components/ValueDisplay';
import SizeUnitToggleButton from 'src/components/TradeConfirmationModal/SizeUnitToggleButton';
import useSpotTradeConfirmationModal from 'src/components/TradeConfirmationModal/hooks/useSpotTradeConfirmationModal';

interface Props {
	tradeFormDetails: TradeFormDetails;
}

export const SpotTradeConfirmationModal = ({ tradeFormDetails }: Props) => {
	const {
		orderType,
		priceBoxValue,
		secondaryPriceBoxValue,
		tradeSide,
		tickSizeDecimalPlaces,
		actionValue,
		sizeDisplayLabel,
		sizeDisplayValue,
		actionValueClassName,
		estimatedLiquidationPriceUi,
		reduceOnly,
		postOnly,
		isSubmitting,
		handleClose,
		handleConfirmation,
	} = useSpotTradeConfirmationModal({ tradeFormDetails });

	return (
		<TradeConfirmationModalWrapper
			tradeSide={tradeSide}
			isSubmitting={isSubmitting}
			handleConfirmation={handleConfirmation}
			handleClose={handleClose}
			reduceOnly={reduceOnly}
		>
			<div className={TRADE_CONFIRMATION_MODAL_DETAILS_CONTAINER_CLASSNAME}>
				<ValueDisplay.Default
					label="Action"
					value={actionValue}
					valueClassName={actionValueClassName}
				/>
				<ValueDisplay.Default
					label={sizeDisplayLabel}
					value={
						<div className="flex items-center gap-x-xs">
							{sizeDisplayValue}
							<SizeUnitToggleButton />
						</div>
					}
				/>
				{orderType === 'market' && (
					<ValueDisplay.Default label="Price" value="Market" />
				)}
				{['limit', 'stopLimit', 'stopMarket', 'takeProfitLimit'].includes(
					orderType
				) && (
					<ValueDisplay.Default
						label={orderType === 'limit' ? 'Limit price' : 'Trigger price'}
						value={formatText(
							priceBoxValue,
							withDecimals(PRESETS.usdTruncate, tickSizeDecimalPlaces)
						)}
					/>
				)}
				{(orderType === 'stopLimit' || orderType === 'takeProfitLimit') && (
					<ValueDisplay.Default
						label="Limit price"
						value={formatText(
							secondaryPriceBoxValue,
							withDecimals(PRESETS.usdTruncate, tickSizeDecimalPlaces)
						)}
					/>
				)}
				{orderType === 'oracleLimit' && (
					<ValueDisplay.Default
						label="Oracle offset"
						value={getOracleOffsetLabel(priceBoxValue, true)}
					/>
				)}
				{!reduceOnly && (
					<ValueDisplay.Default
						label="Est. liquidation price"
						value={estimatedLiquidationPriceUi}
					/>
				)}
				{(reduceOnly || postOnly) && (
					<ValueDisplay.Default
						label="Flag"
						value={reduceOnly ? 'Reduce only' : 'Post only'}
					/>
				)}
			</div>
		</TradeConfirmationModalWrapper>
	);
};
